import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, LogOut, Truck, CheckCircle2, AlertCircle, MapPin, Phone, Filter, Clock, Calendar, Box, X, UserCog, Search, Eye, Image as ImageIcon, Tag, Lock, Shield, ShieldCheck, Ruler, Weight, Printer, Navigation } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import BaggageTagPrint, { triggerPrint, printBaggageTagInPopup } from '../components/BaggageTagPrint';
import { formatPickupSlot } from '../lib/pickupSlots';
import { formatPickupAddress, formatDeliveryAddress } from '../lib/addressFormat';
import { ViewMapButton } from '../components/ViewMapButton';
import { CallCustomerButton } from '../components/CallCustomerButton';
import { InstallAppButton } from '../components/InstallAppButton';
import { DeliveryOtpModal } from '../components/DeliveryOtpModal';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import EditAuthorisationModal from '../components/EditAuthorisationModal';
import { useLocationBeacon } from '../hooks/useLocationBeacon';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  NEW_BOOKING: { color: '#A5B4FC', label: 'NEW BOOKING', icon: Calendar },
  BOOKED: { color: '#52525B', label: 'BOOKED', icon: Calendar },
  PAID: { color: '#00C853', label: 'PAID', icon: CheckCircle2 },
  SCHEDULED: { color: '#FFB800', label: 'SCHEDULED', icon: Calendar },
  DELAYED_PICKUP: { color: '#DC2626', label: 'DELAYED PICKUP', icon: AlertCircle },
  PICKED_UP: { color: '#FFB800', label: 'PICKED UP', icon: Box },
  IN_TRANSIT: { color: '#FF4500', label: 'IN TRANSIT', icon: Truck },
  ARRIVED_DESTINATION: { color: '#00A651', label: 'ARRIVED AT DESTINATION', icon: MapPin },
  OUT_FOR_DELIVERY: { color: '#FF4500', label: 'OUT FOR DELIVERY', icon: Truck },
  DELAYED_DELIVERY: { color: '#DC2626', label: 'DELAYED DELIVERY', icon: AlertCircle },
  DELIVERED: { color: '#00C853', label: 'DELIVERED', icon: CheckCircle2 },
  DELAYED: { color: '#DC2626', label: 'DELAYED', icon: AlertCircle },
  REROUTED: { color: '#F59E0B', label: 'REROUTED', icon: AlertCircle },
  CANCELLED_BY_CUSTOMER: { color: '#9CA3AF', label: 'CANCELLED BY CUSTOMER', icon: AlertCircle }
};

const STATUS_OPTIONS = ['ALL', 'NEW_BOOKING', 'SCHEDULED', 'DELAYED_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY', 'DELIVERED', 'CANCELLED_BY_CUSTOMER'];

// Agent allowed transitions per current status (NO REROUTED — partner-only).
// Agent handles pickup, the final delivery handover, AND (from Feb 2026)
// marking the bag as ARRIVED_DESTINATION at the destination hub.
// DELIVERED is NEVER offered here — it must always go through the OTP modal.
const STATUS_TRANSITIONS_AGENT = {
  NEW_BOOKING:        ['SCHEDULED', 'CANCELLED_BY_CUSTOMER'],
  BOOKED:             ['NEW_BOOKING', 'SCHEDULED', 'CANCELLED_BY_CUSTOMER'],
  PAID:               ['NEW_BOOKING', 'SCHEDULED', 'CANCELLED_BY_CUSTOMER'],
  SCHEDULED:          ['PICKED_UP', 'DELAYED_PICKUP', 'CANCELLED_BY_CUSTOMER'],
  DELAYED_PICKUP:     ['PICKED_UP', 'CANCELLED_BY_CUSTOMER'],
  PICKED_UP:          ['ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY'],
  IN_TRANSIT:         ['ARRIVED_DESTINATION'],   // agent can mark hub arrival
  ARRIVED_DESTINATION: ['OUT_FOR_DELIVERY', 'DELAYED_DELIVERY'],
  OUT_FOR_DELIVERY:   ['DELAYED_DELIVERY'],   // DELIVERED via OTP modal only
  DELAYED_DELIVERY:   ['OUT_FOR_DELIVERY'],
  DELAYED:            [],
  DELIVERED:          [],
  CANCELLED_BY_CUSTOMER: [],
  REROUTED:           []
};

const OTP_ELIGIBLE_STATES = new Set(['OUT_FOR_DELIVERY', 'DELAYED_DELIVERY']);

const Agent = () => {
  const navigate = useNavigate();
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();
  // Initialise auth state synchronously from localStorage so the back button
  // doesn't flash the login form when a session already exists.
  const _savedAgent = (() => {
    try { return JSON.parse(localStorage.getItem('agent_session') || 'null'); }
    catch { return null; }
  })();
  const [isAuth, setIsAuth] = useState(Boolean(_savedAgent));
  const [agent, setAgent] = useState(_savedAgent);

  // Login form
  const [agentCode, setAgentCode] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Dashboard
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Status update modal
  const [editBooking, setEditBooking] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusLocation, setStatusLocation] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Verify-luggage-details modal
  const [viewingBooking, setViewingBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);  // OTP-authorised manifest edit target

  // Delivery OTP modal
  const [otpBooking, setOtpBooking] = useState(null);
  
  // Printable tag
  const [printingBooking, setPrintingBooking] = useState(null);

  // GPS beacon — agent opts in from the header pill. Persists across reloads.
  const [beaconOn, setBeaconOn] = useState(() => {
    try { return localStorage.getItem('lfj_agent_beacon_on') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('lfj_agent_beacon_on', beaconOn ? '1' : '0'); } catch {}
  }, [beaconOn]);
  const beacon = useLocationBeacon({
    agentCode: agent?.agent_code,
    enabled: isAuth && beaconOn,
  });
  
  const handlePrint = async (booking) => {
    let customer = null;
    if (booking.user_id) {
      try {
        const r = await axios.get(`${API}/users/${booking.user_id}`);
        customer = r.data;
      } catch (e) { console.warn('[agent] fetch customer for print failed:', e); }
    }
    setPrintingBooking({ booking, customer });
    printBaggageTagInPopup(booking, customer);
  };

  useEffect(() => {
    const saved = localStorage.getItem('agent_session');
    if (saved) {
      const data = JSON.parse(saved);
      setAgent(data);
      setIsAuth(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!agent) return;
    setLoading(true);
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/agent/${agent.agent_code}/stats`),
        axios.get(`${API}/agent/${agent.agent_code}/bookings${statusFilter !== 'ALL' ? `?status=${statusFilter}` : ''}`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
    } catch (err) {
      console.error('Failed to fetch agent data:', err);
    } finally {
      setLoading(false);
    }
  }, [agent, statusFilter]);

  useEffect(() => {
    if (isAuth) fetchData();
  }, [isAuth, fetchData]);

  // Escape-key dismiss for the "Verify Luggage" details modal
  useEffect(() => {
    if (!viewingBooking) return;
    const onKey = (e) => { if (e.key === 'Escape') setViewingBooking(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewingBooking]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await axios.post(`${API}/agent/login`, {
        agent_code: agentCode,
        password
      });
      if (response.data.success) {
        const data = {
          agent_code: response.data.agent_code,
          name: response.data.name,
          contact: response.data.contact,
          token: response.data.token
        };
        localStorage.setItem('agent_session', JSON.stringify(data));
        setAgent(data);
        setIsAuth(true);
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('agent_session');
    setIsAuth(false);
    setAgent(null);
    setAgentCode('');
    setPassword('');
  };

  const openUpdateModal = (booking) => {
    // Issue 5 — alert the operator when modifying a still-BOOKED booking.
    if ((booking.tracking_status || 'BOOKED') === 'BOOKED') {
      const ok = window.confirm(
        `Heads up: Booking ${booking.baggage_tag || booking.booking_id} is still in BOOKED status (awaiting payment / scheduling).\n\nAny update you make now may surprise the customer. Continue?`
      );
      if (!ok) return;
    }
    setEditBooking(booking);
    setNewStatus(booking.tracking_status || 'BOOKED');
    setStatusLocation('');
    setStatusNotes('');
    setUpdateError('');
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    setUpdateError('');
    try {
      await axios.post(`${API}/agent/${agent.agent_code}/bookings/${editBooking.booking_id}/status`, {
        status: newStatus,
        location: statusLocation,
        notes: statusNotes
      });
      setEditBooking(null);
      fetchData();
    } catch (err) {
      setUpdateError(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Login Screen
  if (!isAuth) {
    return (
      <div className="theme-sunset flex items-center justify-center p-4">
        <div className="card-brutalist p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <BrandMark size={48} showText={false} className="justify-center mb-3" />
            <UserCog size={36} color="#FF4500" strokeWidth={2} className="mx-auto mb-2" />
            <h1 className="text-2xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              AGENT LOGIN
            </h1>
            <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
              FIELD AGENT PORTAL
            </p>
          </div>
          <label className="block text-xs font-mono uppercase tracking-wide mb-2">AGENT CODE</label>
          <input
            data-testid="agent-code-input"
            type="text"
            value={agentCode}
            onChange={(e) => setAgentCode(e.target.value.toUpperCase())}
            placeholder="Enter your agent code"
            className="w-full mb-4"
          />
          <label className="block text-xs font-mono uppercase tracking-wide mb-2">PASSWORD</label>
          <input
            data-testid="agent-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Enter password"
            className="w-full mb-4"
          />
          {loginError && (
            <p data-testid="agent-login-error" className="text-red-600 text-sm mb-4 font-mono">{loginError}</p>
          )}
          <button
            data-testid="agent-login-btn"
            onClick={handleLogin}
            disabled={loginLoading}
            className="btn-primary w-full py-3 disabled:opacity-50"
          >
            {loginLoading ? 'AUTHENTICATING...' : 'LOGIN'}
          </button>
          <InstallAppButton className="mt-3" label="INSTALL AGENT APP" manifest="/manifest-agent.json" />
        </div>
      </div>
    );
  }

  return (
    <div className="theme-sunset p-4 md:p-8" data-testid="agent-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BrandMark size={36} />
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="px-2 py-1 bg-[#FFB800] text-black text-xs font-mono uppercase border-2 border-black">AGENT</span>
            <div className="flex gap-2 items-center">
              <div className="text-right hidden md:block">
                <div className="text-xs font-mono uppercase text-gray-600">{agent?.agent_code}</div>
                <div className="text-sm font-bold font-mono">{agent?.name}</div>
              </div>
              <button
                data-testid="agent-beacon-toggle"
                onClick={() => setBeaconOn(v => !v)}
                title={
                  beacon.status === 'denied'
                    ? 'Location permission denied — enable it in browser settings'
                    : beacon.status === 'unavailable'
                    ? 'Geolocation not supported on this device'
                    : beaconOn
                      ? 'GPS beacon ON — customers see your live location'
                      : 'GPS beacon OFF — tap to share live location'
                }
                className={`px-3 py-2 flex items-center gap-2 text-xs font-mono font-bold uppercase border-2 border-black ${
                  beaconOn && beacon.status === 'granted' ? 'bg-[#00C853] text-black'
                  : beaconOn ? 'bg-[#FFB800] text-black'
                  : 'bg-white text-black'
                }`}
              >
                <Navigation size={14} className={beaconOn && beacon.status === 'granted' ? 'animate-pulse' : ''} />
                <span className="hidden sm:inline">
                  {beacon.status === 'denied' ? 'GPS BLOCKED'
                    : beacon.status === 'unavailable' ? 'GPS N/A'
                    : beaconOn && beacon.status === 'granted' ? 'GPS LIVE'
                    : beaconOn ? 'GPS…'
                    : 'GPS OFF'}
                </span>
              </button>
              <button data-testid="agent-logout-btn" onClick={handleLogout} className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm">
                <LogOut size={16} />
                LOGOUT
              </button>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
            MY ASSIGNMENTS
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
            {agent?.agent_code} · {bookings.length} TOTAL ASSIGNED
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="agent-stats">
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">TOTAL</div>
              <div className="text-3xl font-black font-mono">{stats.total_bookings}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">ACTIVE</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#FF4500' }}>{stats.active_deliveries}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">DELIVERED</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#00C853' }}>{stats.delivered}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">DELAYED</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#DC2626' }}>{stats.delayed}</div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              data-testid="agent-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="SEARCH BY CUSTOMER NAME OR MOBILE"
              className="w-full pl-10 pr-9 py-2 font-mono text-xs uppercase tracking-wide"
            />
            {searchQuery && (
              <button
                data-testid="agent-search-clear-btn"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={16} />
            <span className="text-xs font-mono uppercase">FILTER:</span>
          </div>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt}
              data-testid={`agent-filter-${opt.toLowerCase()}`}
              onClick={() => setStatusFilter(opt)}
              className={`px-3 py-1 border-2 border-black text-xs font-mono uppercase ${
                statusFilter === opt ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              {opt === 'ALL' ? 'ALL' : STATUS_CONFIG[opt]?.label || opt}
            </button>
          ))}
        </div>

        {/* Bookings Grid */}
        {(() => {
          const q = searchQuery.trim().toLowerCase();
          const filtered = q
            ? bookings.filter(b =>
                (b.customer_name || '').toLowerCase().includes(q) ||
                (b.customer_phone || '').toLowerCase().includes(q)
              )
            : bookings;
          if (loading) return <div className="card-brutalist p-12 text-center loading-pulse font-mono">LOADING...</div>;
          if (bookings.length === 0) {
            return (
              <div className="card-brutalist p-12 text-center">
                <Package size={64} strokeWidth={1.5} color="#52525B" className="mx-auto mb-4" />
                <h2 className="text-xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>NO ASSIGNED BOOKINGS</h2>
                <p className="text-sm text-gray-600 font-mono">You don't have any assignments yet. Contact admin for assignments.</p>
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="card-brutalist p-12 text-center" data-testid="agent-no-search-match">
                <Search size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                <p className="text-sm text-gray-600 font-mono">No bookings match "<strong>{searchQuery}</strong>".</p>
              </div>
            );
          }
          return (
            <>
              {q && (
                <div className="mb-4 text-xs font-mono uppercase text-gray-600" data-testid="agent-search-count">
                  Showing {filtered.length} of {bookings.length}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="agent-bookings-list">
                {filtered.map((b) => {
                  const status = b.tracking_status || 'BOOKED';
                  const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
                  return (
                    <div
                      key={b.booking_id}
                      className="card-brutalist p-6"
                      data-testid={`agent-booking-${b.baggage_tag}`}
                    >
                  <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div>
                      <div className="text-xs font-mono uppercase text-gray-600">BAGGAGE TAG</div>
                      <div className="text-2xl font-black font-mono">{b.baggage_tag}</div>
                    </div>
                    <div
                      className="flex items-center gap-2 px-3 py-1 border-2 border-black"
                      style={{ backgroundColor: STATUS_CONFIG[status]?.color }}
                    >
                      <StatusIcon size={14} color="#000" />
                      <span className="font-bold uppercase text-xs">
                        {STATUS_CONFIG[status]?.label}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 p-3 bg-gray-50 border-2 border-black">
                    <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="font-bold">{b.customer_name}</div>
                        {b.customer_phone && (
                          <div className="text-xs font-mono text-gray-600 mt-1">{b.customer_phone}</div>
                        )}
                      </div>
                      {b.customer_phone && (
                        <CallCustomerButton phone={b.customer_phone} size="xs" />
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="data-label mb-1 flex items-center gap-1">
                      <MapPin size={12} />
                      ROUTE
                    </div>
                    <div className="text-sm font-mono">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="font-bold">{b.pickup_location || 'N/A'}</div>
                        <ViewMapButton booking={b} type="pickup" size="xs" />
                      </div>
                      <div className="text-xs text-gray-600 break-words">{formatPickupAddress(b)}</div>
                      <div className="text-orange-600 font-bold mt-1">↓</div>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="font-bold">{b.delivery_location}</div>
                        <ViewMapButton booking={b} type="delivery" size="xs" />
                      </div>
                      {formatDeliveryAddress(b) && (
                        <div className="text-xs text-gray-600 break-words">{formatDeliveryAddress(b)}</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div>
                      <div className="data-label text-[10px]">BAGS</div>
                      <div className="data-value text-sm">{b.luggage_details.quantity || (b.luggage_details.bags?.length || 0)}</div>
                    </div>
                    <div>
                      <div className="data-label text-[10px]">WEIGHT</div>
                      <div className="data-value text-sm">{b.luggage_details.weight} KG</div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {OTP_ELIGIBLE_STATES.has(status) && (
                      <button
                        data-testid={`agent-deliver-otp-${b.baggage_tag}`}
                        onClick={() => setOtpBooking(b)}
                        className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 bg-[#00C853]"
                        title="Verify customer via OTP and mark delivered"
                      >
                        <Shield size={14} />
                        DELIVER & VERIFY (OTP)
                      </button>
                    )}
                    <button
                      data-testid={`agent-view-details-${b.baggage_tag}`}
                      onClick={() => setViewingBooking(b)}
                      className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                    >
                      <Eye size={14} />
                      VIEW & VERIFY DETAILS
                    </button>
                    <button
                      data-testid={`agent-print-tag-${b.baggage_tag}`}
                      onClick={() => handlePrint(b)}
                      className="btn-secondary py-2.5 px-3 text-sm flex items-center gap-1"
                      title="Print baggage tag"
                    >
                      <Printer size={14} />
                      PRINT
                    </button>
                  </div>
                  </div>
                );
              })}
              </div>
            </>
          );
        })()}
      </div>

      {/* Update Status Modal */}
      {editBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="agent-update-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                UPDATE STATUS
              </h2>
              <button onClick={() => setEditBooking(null)} className="hover:text-orange-600">
                <X size={24} />
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
              <div className="text-xl font-black font-mono">{editBooking.baggage_tag}</div>
              <div className="text-xs font-mono text-gray-600 mt-2">{editBooking.customer_name} · {editBooking.customer_phone}</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  NEW STATUS
                  <span className="ml-2 text-gray-500 font-normal normal-case">(from {STATUS_CONFIG[editBooking.tracking_status || 'BOOKED']?.label || editBooking.tracking_status})</span>
                </label>
                {(() => {
                  const allowed = STATUS_TRANSITIONS_AGENT[editBooking.tracking_status || 'BOOKED'] || [];
                  if (allowed.length === 0) {
                    return (
                      <div className="border-2 border-black p-4 bg-gray-100 text-xs font-mono uppercase text-gray-600" data-testid="agent-status-no-transitions">
                        TERMINAL STATE — NO FURTHER STATUS CHANGES ALLOWED FROM "{STATUS_CONFIG[editBooking.tracking_status]?.label || editBooking.tracking_status}".
                      </div>
                    );
                  }
                  return (
                    <div data-testid="agent-status-radio-group" className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border-2 border-black p-2 bg-white">
                      {allowed.map(s => {
                        const cfg = STATUS_CONFIG[s] || {};
                        const checked = newStatus === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`agent-status-radio-${s.toLowerCase()}`}
                            onClick={() => setNewStatus(s)}
                            className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors ${checked ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
                          >
                            <span className={`inline-block w-3 h-3 rounded-full border-2 border-black ${checked ? 'bg-black' : 'bg-white'}`}></span>
                            <span className="flex-1">{cfg.label || s}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">NOTES</label>
                <textarea
                  data-testid="agent-status-notes"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Additional details"
                  rows={3}
                  className="w-full"
                />
              </div>
              {updateError && (
                <p data-testid="agent-update-error" className="text-red-600 text-sm font-mono">{updateError}</p>
              )}
              <button
                data-testid="agent-confirm-update"
                onClick={handleUpdateStatus}
                disabled={updating || newStatus === editBooking.tracking_status || (STATUS_TRANSITIONS_AGENT[editBooking.tracking_status || 'BOOKED'] || []).length === 0}
                className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'UPDATING...' : (newStatus === editBooking.tracking_status ? 'SELECT A NEW STATUS' : 'UPDATE & NOTIFY CUSTOMER')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View & Verify Luggage Details Modal */}
      {viewingBooking && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setViewingBooking(null); }}
          data-testid="agent-view-details-overlay"
        >
          <div className="card-brutalist bg-white max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="agent-view-details-modal">
            <div className="flex items-center justify-between p-6 pb-4 border-b-2 border-black bg-white sticky top-0 rounded-t-[inherit]">
              <div>
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  VERIFY LUGGAGE
                </h2>
                <p className="text-xs font-mono uppercase text-gray-600">
                  CROSS-CHECK ALL ITEMS BEFORE STATUS UPDATE
                </p>
              </div>
              <button data-testid="agent-close-details-modal" onClick={() => setViewingBooking(null)} aria-label="Close details" className="p-2 -m-2 hover:text-orange-600">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 pt-4 flex-1">

            {/* Booking Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
                <div className="text-xl font-black font-mono">{viewingBooking.baggage_tag}</div>
              </div>
              <div className="p-3 bg-gray-50 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-bold">{viewingBooking.customer_name}</div>
                    {viewingBooking.customer_phone && (
                      <div className="text-xs font-mono text-gray-600 mt-1">{viewingBooking.customer_phone}</div>
                    )}
                  </div>
                  {viewingBooking.customer_phone && (
                    <CallCustomerButton phone={viewingBooking.customer_phone} size="sm" />
                  )}
                </div>
              </div>
            </div>

            {/* Route */}
            <div className="p-3 bg-gray-50 border-2 border-black mb-4">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                <MapPin size={10} />ROUTE
              </div>
              <div className="text-sm font-mono space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">PICKUP — {viewingBooking.pickup_location}</span>
                    <span className="text-gray-600"> ({formatPickupAddress(viewingBooking)})</span>
                  </div>
                  <ViewMapButton booking={viewingBooking} type="pickup" size="sm" />
                </div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">DROP — {viewingBooking.delivery_location}</span>
                    {formatDeliveryAddress(viewingBooking) && (
                      <span className="text-gray-600"> ({formatDeliveryAddress(viewingBooking)})</span>
                    )}
                  </div>
                  <ViewMapButton booking={viewingBooking} type="delivery" size="sm" />
                </div>
              </div>
            </div>

            {(viewingBooking.scheduled_pickup_date || viewingBooking.scheduled_pickup_time) && (
              <div className="p-3 bg-[#FFF7E6] border-2 border-black mb-4" data-testid="agent-scheduled-pickup">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                  <Calendar size={10}/>SCHEDULED PICKUP
                </div>
                <div className="text-sm font-bold font-mono">
                  {viewingBooking.scheduled_pickup_date || '—'}{viewingBooking.scheduled_pickup_time ? ` · ${formatPickupSlot(viewingBooking.scheduled_pickup_time)}` : ''}
                </div>
              </div>
            )}

            {/* Bag-by-Bag Details */}
            <div className="mb-4">
              <h3 className="text-sm font-black uppercase mb-3 font-mono">
                BAGS ({viewingBooking.luggage_details.bags?.length || 0})
              </h3>
              <div className="space-y-4">
                {(viewingBooking.luggage_details.bags || []).map((bag, idx) => (
                  <div key={idx} className="border-2 border-black p-4 bg-white" data-testid={`agent-bag-detail-${idx}`}>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-dashed border-gray-300">
                      <div className="font-mono uppercase font-black text-base">BAG #{idx + 1}</div>
                      <div className="px-2 py-1 bg-black text-white text-xs font-mono uppercase">
                        {(bag.bag_type || 'BAG').toUpperCase()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3 text-xs">
                      <div>
                        <div className="data-label flex items-center gap-1"><Tag size={10}/>BRAND/COMPANY</div>
                        <div className="font-bold">{bag.brand || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label">COLOR</div>
                        <div className="font-bold">{bag.color || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label">MATERIAL</div>
                        <div className="font-bold uppercase">{(bag.material || 'soft_fabric').replace('_', ' ')}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Weight size={10}/>WEIGHT</div>
                        <div className="font-bold">{bag.weight} KG</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Ruler size={10}/>DIMENSIONS</div>
                        <div className="font-bold">{bag.dimensions || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Shield size={10}/>HANDLING</div>
                        <div className="font-bold uppercase">{bag.handling_type || 'standard'}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Lock size={10}/>LOCK</div>
                        <div className="font-bold uppercase">{bag.lock_type || 'none'}</div>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <div className="data-label">DISTINGUISHING MARKS</div>
                        <div className="font-bold">{bag.distinguishing_marks || '—'}</div>
                      </div>
                    </div>

                    {/* Photos */}
                    <div>
                      <div className="data-label mb-2 flex items-center gap-1">
                        <ImageIcon size={10}/>
                        CUSTOMER PHOTOS ({(bag.images || []).length})
                      </div>
                      {(bag.images || []).length === 0 ? (
                        <div className="text-xs font-mono text-gray-500 italic p-3 border-2 border-dashed border-gray-300 text-center">
                          No photos uploaded by customer
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(bag.images || []).map((img, imgIdx) => (
                            <button
                              key={imgIdx}
                              type="button"
                              onClick={() => openPhoto(bag.images || [], imgIdx)}
                              data-testid={`agent-bag-${idx}-photo-${imgIdx}`}
                              className="block border-2 border-black overflow-hidden hover:shadow-lg transition-shadow cursor-zoom-in"
                              title="Click to enlarge"
                            >
                              <img
                                src={img}
                                alt={`Bag ${idx + 1} photo ${imgIdx + 1}`}
                                className="w-full h-24 object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions — sticky footer so buttons remain reachable even when
                content overflows the modal height. */}
            </div>
            <div className="p-6 pt-4 border-t-2 border-black bg-white flex flex-col sm:flex-row gap-3 sticky bottom-0 rounded-b-[inherit]">
              <button
                data-testid="agent-cancel-verify"
                onClick={() => setViewingBooking(null)}
                className="btn-secondary px-4 py-3 text-xs flex-1"
              >
                CLOSE
              </button>
              {(viewingBooking?.tracking_status || '').toUpperCase() === 'SCHEDULED' && (
                <button
                  data-testid="agent-request-edit"
                  onClick={() => {
                    setEditingBooking(viewingBooking);
                    setViewingBooking(null);
                  }}
                  className="btn-secondary px-4 py-3 text-xs flex-1 flex items-center justify-center gap-2 border-orange-500 text-orange-600"
                  title="Edit manifest with customer OTP authorisation (SCHEDULED bookings only)"
                >
                  <ShieldCheck size={14} /> REQUEST EDIT (OTP)
                </button>
              )}
              <button
                data-testid="agent-confirm-and-update"
                onClick={() => {
                  const booking = viewingBooking;
                  setViewingBooking(null);
                  openUpdateModal(booking);
                }}
                className="btn-primary px-4 py-3 text-xs flex-1 flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={14} />
                CONFIRM & UPDATE STATUS
              </button>
            </div>
          </div>
        </div>
      )}

      {editingBooking && (
        <EditAuthorisationModal
          role="agent"
          roleCode={agentCode}
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={(b) => { if (b) setEditingBooking((prev) => ({ ...prev, ...b })); fetchData(); }}
        />
      )}

      {/* Printable baggage tag */}
      {printingBooking && (
        <BaggageTagPrint booking={printingBooking.booking} customer={printingBooking.customer} />
      )}

      {/* Delivery OTP Modal */}
      {otpBooking && agent && (
        <DeliveryOtpModal
          role="agent"
          roleCode={agent.agent_code}
          booking={otpBooking}
          onClose={() => setOtpBooking(null)}
          onDelivered={fetchData}
        />
      )}
      {photoGallery}
    </div>
  );
};

export default Agent;
